import {
  Box,
  Button,
  Grid,
  MenuItem,
  Slider,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  useState,
  type FC,
  ChangeEvent,
  PointerEvent,
  WheelEvent,
  useMemo,
  useEffect,
  RefObject,
} from "react";
import { usePhotoEditor } from "react-photo-editor";
import { useMeasure } from "../../hooks/useMeasure";
import {
  API_ASYNC_INFER_SEGMENT_ANY,
  DEFAULT_LASSO_LINE_WIDTH,
  DEFAULT_LINE_COLOR,
  ERR_MSG_NOT_GET_REFER_BASE64,
  ERR_MSG_NOT_LOGIN,
  ERR_MSG_NO_TASK_ID,
  ERR_MSG_REFER_LASSO_POINTS_NOT_ENOUGH,
  INFER_TASK_STATUS_ENUM,
  MAGIC_SHORTCUT_PARAM_TYPE_ENUM,
  MAX_UPLOAD_IMG_SIZE_ENUM,
  REFERENCE_ACTIONS_ENUM,
  SEGMENT_TYPE_ENUM,
} from "../../utils/constants";
import {
  LassoPointType,
  fetchFileByImgUrl,
  imgFileToBase64,
  resizeImage,
} from "../../utils/image.util";
import { ImgUploadBtn } from "../buttons/ImgUploadBtn";
import { ActionItem } from "../MagicPage";
import {
  SegmentAnyAPIRequestBodyType,
  cancelTask,
  getTaskResultEvery3s,
} from "../../utils/http.util";
import { MainMagicShortcutType } from "../main-page/Shortcuts";

type ReferenceActionsProps = {
  magicShortcutObj: MainMagicShortcutType | null;
  actionsDisabled: boolean;
  showPromptMsg: (msg: string) => void;
  onMagicGeneratingChange: (generating: boolean) => void;
  onCanvasRefChange: (canvasRef: RefObject<HTMLCanvasElement | null>) => void;
  magicUrl: string;
};

export const ReferenceActions: FC<ReferenceActionsProps> = (props) => {
  // responsive UI
  const theme = useTheme();
  const notPhone = useMediaQuery(theme.breakpoints.up("sm"));

  // 加载到canvas里的参考图文件
  const [fileRefer, setFileRefer] = useState<File | undefined>(undefined);
  const [canvasBoxRef, canvasBoxWH] = useMeasure();
  // 用于设置参考图 canvas styles 里的 width/height
  const [canvasReferWidth, setCanvasReferWidth] = useState(0);
  const [canvasReferHeight, setCanvasReferHeight] = useState(0);

  const [imgRefer, setImgRefer] = useState<HTMLImageElement | null>(null);
  const [actionType, setActionType] = useState("");
  const [segmenting, setSegmenting] = useState(false);
  const [lassoPoints, setLassoPoints] = useState<LassoPointType[]>([]);

  const [taskId, setTaskId] = useState("");
  const [taskStatus, setTaskStatus] = useState<string>(
    INFER_TASK_STATUS_ENUM.down
  );
  const [totalTasks, setTotalTasks] = useState(0);

  const {
    canvasRef: canvasRef_refer,
    handlePointerDown: handleReferPointerDown,
    handlePointerUp: handleReferPointerUp,
    handlePointerMove: handleReferPointerMove,
    handleWheel: handleReferWheel,
    resetFilters: resetReferFilters,
    generateEditedFile: generateEditedFileRefer,
    rotate: rotateRefer,
    setRotate: setRotateRefer,
    zoom: zoomRefer,
    setZoom: setZoomRefer,
    brightness: brightnessRefer,
    setBrightness: setBrightnessRefer,
  } = usePhotoEditor({ file: fileRefer });

  const handleReferImgUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const curFile = event.target.files?.[0];
    console.log(" -> ReferenceActions-handleReferImgUpload, file:", curFile);

    if (!curFile) return;

    setActionType("");

    // 按参考图尺寸等比例缩放显示在canvas容器内
    const curImg = new Image();
    setImgRefer(curImg);

    const url = URL.createObjectURL(curFile);
    console.log(" -> ReferenceActions-handleReferImgUpload, url:", url);

    curImg.src = url;
    curImg.onload = () => {
      console.log(
        " -> ReferenceActions-handleReferImgUpload, curImg.width:",
        curImg.width,
        ", curImg.height:",
        curImg.height
      );

      const { width, height } = curImg;
      if (
        width > MAX_UPLOAD_IMG_SIZE_ENUM.width ||
        height > MAX_UPLOAD_IMG_SIZE_ENUM.height
      ) {
        props.showPromptMsg(`上传的参考图大于4K！将缩小到4K。`);

        resizeImage(
          curImg,
          MAX_UPLOAD_IMG_SIZE_ENUM.width,
          MAX_UPLOAD_IMG_SIZE_ENUM.height,
          (resizedImgFile, resizedImgWidth, resizedImgHeight) => {
            setFileRefer(resizedImgFile);

            const wRatio = canvasBoxWH.width / resizedImgWidth;
            const hRatio = canvasBoxWH.height / resizedImgHeight;
            const fitRatio = hRatio < wRatio ? hRatio : wRatio; // 得出容器的大小与图片大小的比例
            setCanvasReferWidth(Math.round(resizedImgWidth * fitRatio));
            setCanvasReferHeight(Math.round(resizedImgHeight * fitRatio));
          }
        );
      } else {
        setFileRefer(curFile);

        const wRatio = canvasBoxWH.width / curImg.width;
        const hRatio = canvasBoxWH.height / curImg.height;
        const fitRatio = hRatio < wRatio ? hRatio : wRatio; // 得出容器的大小与图片大小的比例
        setCanvasReferWidth(Math.round(curImg.width * fitRatio));
        setCanvasReferHeight(Math.round(curImg.height * fitRatio));
      }

      // 释放对象
      URL.revokeObjectURL(url);
    };
  };

  const handleReferLassoPointerDown = (e: PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef_refer.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    setLassoPoints((prevLassoPoints) => [...prevLassoPoints, { x, y }]);

    const context = canvas?.getContext("2d");
    if (!context) return;

    context.globalCompositeOperation = "source-over";
    context.fillStyle = DEFAULT_LINE_COLOR;
    context.beginPath();
    context.arc(x, y, (DEFAULT_LASSO_LINE_WIDTH * scaleX) / 2, 0, Math.PI * 2);
    context.fill();

    const pointsLength = lassoPoints.length;
    if (pointsLength > 0) {
      context.strokeStyle = DEFAULT_LINE_COLOR;
      context.lineWidth = DEFAULT_LASSO_LINE_WIDTH * 2;
      context.beginPath();
      context.moveTo(x, y);
      context.lineTo(
        lassoPoints[pointsLength - 1].x,
        lassoPoints[pointsLength - 1].y
      );
      context.closePath();
      context.stroke();
    }
  };

  const actions: ActionItem[] = useMemo(
    () => [
      {
        label: REFERENCE_ACTIONS_ENUM.zoom,
        value: zoomRefer,
        onChange: (_e: Event, newValue: number) => setZoomRefer(newValue),
        min: 0.1,
        max: 3,
        step: 0.1,
      },
      {
        label: REFERENCE_ACTIONS_ENUM.brightness,
        value: brightnessRefer,
        onChange: (_e: Event, newValue: number) => setBrightnessRefer(newValue),
        min: 0,
        max: 200,
      },
      {
        label: REFERENCE_ACTIONS_ENUM.rotate,
        value: rotateRefer,
        onChange: (_e: Event, newValue: number) => setRotateRefer(newValue),
        min: 0,
        max: 360,
      },
      {
        label: REFERENCE_ACTIONS_ENUM.lasso,
      },
      {
        label: REFERENCE_ACTIONS_ENUM.segment,
      },
    ],
    // eslint-disable-next-line
    [zoomRefer, brightnessRefer, rotateRefer]
  );

  const handleReferCancelTaskClick = async () => {
    console.log(
      "-> ReferenceActions-handleReferCancelTaskClick, totalTasks:",
      totalTasks,
      ", taskId:",
      taskId
    );

    if (taskId) {
      const data = await cancelTask(taskId);
      if (data === ERR_MSG_NOT_LOGIN) {
        props.showPromptMsg(ERR_MSG_NOT_LOGIN);
      } else if (typeof data === "string" && data.includes(":")) {
        props.showPromptMsg(data);
      } else {
        setTaskStatus(INFER_TASK_STATUS_ENUM.down);
        setTaskId("");
        setTotalTasks(0);
      }
    } else {
      props.showPromptMsg(ERR_MSG_NO_TASK_ID);
    }
  };

  const handleReferLassoDoneClick = () => {
    setSegmenting(false);

    console.log(
      " -> ReferenceActions-handleReferLassoDoneClick, lassoPoints:",
      lassoPoints
    );

    if (lassoPoints.length < 3) {
      props.showPromptMsg(ERR_MSG_REFER_LASSO_POINTS_NOT_ENOUGH);
    } else {
      var canvas = canvasRef_refer.current;
      if (!canvas) return;

      var ctx = canvas.getContext("2d");
      if (!ctx) return;

      if (imgRefer === null) return;

      // 保存当前状态
      ctx.save();

      // 定义裁剪区域
      ctx.lineWidth = DEFAULT_LASSO_LINE_WIDTH * 2;
      ctx.beginPath();
      ctx.moveTo(lassoPoints[0].x, lassoPoints[0].y);
      for (let i = 1; i < lassoPoints.length; i++) {
        ctx.lineTo(lassoPoints[i].x, lassoPoints[i].y);
      }
      ctx.closePath();
      ctx.stroke();
      ctx.clip();

      // 恢复裁剪区域之外的状态
      ctx.restore();

      // 涂抹白色区域
      ctx.fillStyle = DEFAULT_LINE_COLOR;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // 再次绘制裁剪区域内的图片
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(lassoPoints[0].x, lassoPoints[0].y);
      for (let j = 1; j < lassoPoints.length; j++) {
        ctx.lineTo(lassoPoints[j].x, lassoPoints[j].y);
      }
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(imgRefer, 0, 0, canvas.width, canvas.height);
      ctx.restore();

      setLassoPoints([]);
    }
  };

  // segment
  const handleReferTaskIdUpdate = (tid: string) => setTaskId(tid);

  const handleReferTaskStatusUpdate = (status: string, idx: number) => {
    console.log(
      " -> ReferenceActions-handleReferTaskStatusUpdate, status:",
      status,
      ", idx:",
      idx
    );
    setTaskStatus(status);
    setTotalTasks(idx);
  };

  const handleReferGetTaskResult = async (data: any) => {
    console.log(
      "-> ReferenceActions-handleReferGetTaskResult, response:",
      data
    );

    setSegmenting(false);

    if (data === ERR_MSG_NOT_LOGIN) {
      props.showPromptMsg(ERR_MSG_NOT_LOGIN);
    } else if (typeof data === "string" && data.includes(":")) {
      // "5: task a241ef337ab7987d has been canceled"
      const code = data.split(": ")[0];
      if (parseInt(code) === 5) {
        setTaskStatus(INFER_TASK_STATUS_ENUM.down);
        setTaskId("");
        setTotalTasks(0);
      } else {
        props.showPromptMsg(data);
      }
    } else {
      if (data?.result && data.result?.rmbg_rgba) {
        // "segment_prompt": "rmbg", - 这种，返回 rmbg_mask, rmbg_rgba 两张图的 url
        // 替换参考图
        const newFile = await fetchFileByImgUrl(
          data.result?.rmbg_rgba,
          "new_refer.jpg"
        );
        setFileRefer(newFile);
      }
    }
  };

  const handleActionsChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const curActionType = e.target.value;
    setActionType(e.target.value);

    if (curActionType === REFERENCE_ACTIONS_ENUM.segment) {
      // 前景分割 call segment_any API
      setSegmenting(true);

      const editedFile = await generateEditedFileRefer();
      if (editedFile) {
        imgFileToBase64(editedFile, "Edited Reference File", async (result) => {
          if (typeof result === "string" && result) {
            const requestBody: SegmentAnyAPIRequestBodyType = {
              init_image: result.split(",")[1],
              segment_prompt: SEGMENT_TYPE_ENUM.rmbg,
              prompt: "",
            };

            const taskResultResp = await getTaskResultEvery3s(
              API_ASYNC_INFER_SEGMENT_ANY,
              requestBody,
              handleReferTaskIdUpdate,
              handleReferTaskStatusUpdate
            );

            handleReferGetTaskResult(taskResultResp);
          } else {
            props.showPromptMsg(`前景分割：${ERR_MSG_NOT_GET_REFER_BASE64}！`);
          }
        });
      }
    }
  };

  useEffect(() => {
    if (
      taskStatus === INFER_TASK_STATUS_ENUM.inProgress ||
      (taskStatus === INFER_TASK_STATUS_ENUM.waiting && totalTasks < 1)
    ) {
      props.onMagicGeneratingChange(true);
    } else {
      props.onMagicGeneratingChange(false);
    }
    // eslint-disable-next-line
  }, [taskStatus]);

  useEffect(() => {
    if (!props.magicUrl) {
      setFileRefer(undefined);
      setImgRefer(null);

      resetReferFilters();
      setActionType("");
      setSegmenting(false);
      setLassoPoints([]);

      setTaskId("");
      setTaskStatus("");
      setTotalTasks(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.magicUrl]);

  useEffect(() => {
    if (canvasRef_refer.current) {
      props.onCanvasRefChange(canvasRef_refer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasRef_refer]);

  // window resize
  useEffect(() => {
    if (fileRefer) {
      const curImg = new Image();
      const url = URL.createObjectURL(fileRefer);
      curImg.src = url;
      curImg.onload = () => {
        var wRatio = canvasBoxWH.width / curImg.width;
        var hRatio = canvasBoxWH.height / curImg.height;
        var fitRatio = hRatio < wRatio ? hRatio : wRatio; // 得出容器的大小与图片大小的比例
        setCanvasReferWidth(Math.round(curImg.width * fitRatio));
        setCanvasReferHeight(Math.round(curImg.height * fitRatio));

        // 释放对象
        URL.revokeObjectURL(url);
      };
    }
    // eslint-disable-next-line
  }, [canvasBoxWH.width, canvasBoxWH.height]);

  return (
    <Grid
      size={{ xs: 12 }}
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1,
      }}
    >
      <Grid container spacing={2} sx={{ width: "100%" }}>
        <Grid
          size={{ xs: 12 }}
          ref={canvasBoxRef}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            height: notPhone ? "calc(100vh - 410px)" : "400px",
            border:
              Boolean(props.magicShortcutObj) && !fileRefer
                ? "none"
                : "1px solid lightgrey",
            margin:
              Boolean(props.magicShortcutObj) && !fileRefer ? 0 : "0 16px",
            borderRadius: "4px",
          }}
        >
          {Boolean(props.magicShortcutObj?.teach) && (
            <img
              src={props.magicShortcutObj?.teach}
              alt="快捷功能教学图"
              style={{
                position: "absolute",
                visibility: !!fileRefer ? "hidden" : "visible",
                maxWidth: "100%",
                maxHeight: "100%",
                border: `1px solid ${theme.palette.secondary.dark}`,
                boxShadow: `0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)`,
                borderRadius: "5px",
              }}
            />
          )}

          <canvas
            id="magic-page-refer"
            style={{
              width: canvasReferWidth > 0 ? `${canvasReferWidth}px` : "inherit",
              height:
                canvasReferHeight > 0 ? `${canvasReferHeight}px` : "inherit",
              touchAction: "none",
              cursor:
                canvasReferWidth > 0 &&
                (actionType === REFERENCE_ACTIONS_ENUM.zoom ||
                  actionType === REFERENCE_ACTIONS_ENUM.brightness ||
                  actionType === REFERENCE_ACTIONS_ENUM.rotate)
                  ? "grab"
                  : "default",
              position: "absolute",
              zIndex: 0,
              visibility: !!fileRefer ? "visible" : "hidden",
              border:
                props.magicShortcutObj?.params.init_image ===
                MAGIC_SHORTCUT_PARAM_TYPE_ENUM.required
                  ? `1px solid ${theme.palette.secondary.light}`
                  : `1px solid ${theme.palette.primary.light}`,
            }}
            ref={canvasRef_refer}
            onPointerDown={(e: PointerEvent<HTMLCanvasElement>) => {
              if (
                actionType === REFERENCE_ACTIONS_ENUM.zoom ||
                actionType === REFERENCE_ACTIONS_ENUM.brightness ||
                actionType === REFERENCE_ACTIONS_ENUM.rotate
              ) {
                handleReferPointerDown(e);
              } else if (actionType === REFERENCE_ACTIONS_ENUM.lasso) {
                handleReferLassoPointerDown(e);
              } else {
                return;
              }
            }}
            onPointerMove={(e: PointerEvent<HTMLCanvasElement>) => {
              if (
                actionType === REFERENCE_ACTIONS_ENUM.zoom ||
                actionType === REFERENCE_ACTIONS_ENUM.brightness ||
                actionType === REFERENCE_ACTIONS_ENUM.rotate
              ) {
                handleReferPointerMove(e);
              } else {
                return;
              }
            }}
            onPointerUp={() => {
              if (
                actionType === REFERENCE_ACTIONS_ENUM.zoom ||
                actionType === REFERENCE_ACTIONS_ENUM.brightness ||
                actionType === REFERENCE_ACTIONS_ENUM.rotate
              ) {
                handleReferPointerUp();
              } else {
                return;
              }
            }}
            onWheel={(e: WheelEvent<HTMLCanvasElement>) => {
              if (
                actionType === REFERENCE_ACTIONS_ENUM.zoom ||
                actionType === REFERENCE_ACTIONS_ENUM.brightness ||
                actionType === REFERENCE_ACTIONS_ENUM.rotate
              ) {
                handleReferWheel(e);
              } else {
                return;
              }
            }}
          />
        </Grid>

        <Grid
          size={{ xs: 12 }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            margin: "0 16px",
            flexWrap: notPhone ? "nowrap" : "wrap",
          }}
        >
          <TextField
            select
            label="参考图操作"
            placeholder="参考图操作"
            size="small"
            value={actionType}
            onChange={handleActionsChange}
            sx={{ minWidth: "120px" }}
            disabled={props.actionsDisabled || segmenting || !fileRefer}
            color={Boolean(props.magicShortcutObj) ? "secondary" : "primary"}
          >
            {actions.map((act, idx) => (
              <MenuItem key={`reference-actions-type-${idx}`} value={act.label}>
                {act.label}
              </MenuItem>
            ))}
          </TextField>

          {actions
            .filter((act) => act.label === actionType)
            .map(
              (act, idx) =>
                act.onChange && (
                  <Slider
                    key={`reference-actions-slider-${idx}`}
                    value={act.value}
                    onChange={act.onChange}
                    min={act.min ?? 0}
                    max={act.max ?? 100}
                    step={act.step ?? 1}
                    size="small"
                    disabled={props.actionsDisabled}
                    color={
                      Boolean(props.magicShortcutObj) ? "secondary" : "primary"
                    }
                  />
                )
            )}

          {actionType === REFERENCE_ACTIONS_ENUM.lasso && (
            <Button
              size="small"
              variant="contained"
              onClick={handleReferLassoDoneClick}
              disabled={segmenting || lassoPoints.length < 3}
              color={Boolean(props.magicShortcutObj) ? "secondary" : "primary"}
            >
              确定
            </Button>
          )}

          {actionType === REFERENCE_ACTIONS_ENUM.segment && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {taskStatus === INFER_TASK_STATUS_ENUM.waiting && (
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handleReferCancelTaskClick}
                  sx={{ minWidth: "60px" }}
                  size="small"
                >
                  取消
                </Button>
              )}

              {taskStatus === INFER_TASK_STATUS_ENUM.inProgress && (
                <Typography
                  variant="body2"
                  color={
                    Boolean(props.magicShortcutObj) ? "secondary" : "primary"
                  }
                  noWrap
                >
                  {`前景分割中...`}
                </Typography>
              )}
              {taskStatus === INFER_TASK_STATUS_ENUM.waiting && (
                <Typography variant="body2" color="warning" noWrap>
                  {`排队中，${totalTasks}个任务`}
                </Typography>
              )}
            </Box>
          )}

          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              resetReferFilters();
              setActionType("");
              setLassoPoints([]);
              setFileRefer(undefined);
              setImgRefer(null);
            }}
            disabled={!fileRefer || props.actionsDisabled || segmenting}
            sx={{ minWidth: "60px" }}
            color={Boolean(props.magicShortcutObj) ? "secondary" : "primary"}
          >
            清空
          </Button>

          <ImgUploadBtn
            onImgUpload={handleReferImgUpload}
            label="上传"
            variant="contained"
            isFullWidth={false}
            sx={{ minWidth: "80px", marginTop: 0 }}
            size="small"
            disabled={props.actionsDisabled || segmenting}
            color={Boolean(props.magicShortcutObj) ? "secondary" : "primary"}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
